import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import { SALONS } from './data';

// VISIT — three salon "business cards" + appointment form. NO hero image, NO carousel.
export default function Visit() {
  return (
    <div className="bg-[#F2EBDA] text-[#0E0E0E]">
      {/* Masthead */}
      <div className="border-b border-[#0E0E0E]/10">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-12 py-12">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B2D26] mb-3">— Section 05 / The salons</p>
          <h1 className="font-serif text-5xl lg:text-7xl leading-[0.95]">Three rooms, <span className="italic text-[#7B2D26]">always quiet</span>.</h1>
          <p className="font-serif text-lg lg:text-xl italic text-[#0E0E0E]/60 mt-6 max-w-2xl">Each salon holds about thirty pieces and one of us behind the desk. Visits are by appointment, complimentary, and unhurried.</p>
        </div>
      </div>

      {/* Three "business cards" — formal, mono-type, hand-drawn line dividers */}
      <section className="max-w-[1440px] mx-auto px-8 lg:px-12 py-16 grid md:grid-cols-3 gap-px bg-[#0E0E0E]/10">
        {SALONS.map((s, i) => (
          <motion.div key={s.city} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="bg-[#F2EBDA] p-10 lg:p-14 border-l-2 border-[#7B2D26]/30">
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B2D26] mb-2">Salon no. 0{i + 1}</p>
            <h2 className="font-serif text-5xl mb-8">{s.city}</h2>
            {/* Address block */}
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#0E0E0E]/45 mb-2">Address</p>
            <p className="font-serif text-lg leading-snug text-[#0E0E0E]/85 mb-8" dangerouslySetInnerHTML={{ __html: s.addr }} />
            {/* Hours table — distinctive vs cards */}
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#0E0E0E]/45 mb-3">Hours</p>
            <table className="w-full font-mono text-[12px] mb-8">
              <tbody>
                {[['Mon', i === 0 ? 'Closed' : '10–18'], ['Tue–Fri', '10–18'], ['Saturday', '10–17'], ['Sunday', 'Closed'], ['Other', 'By appointment']].map(([d, h]) => (
                  <tr key={d} className="border-b border-[#0E0E0E]/8"><td className="py-2 text-[#0E0E0E]/55 uppercase tracking-[0.15em]">{d}</td><td className="py-2 text-right">{h}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-col gap-2 font-mono text-[12px]">
              <a href={`tel:${s.tel}`} className="text-[#0E0E0E]/75 hover:text-[#7B2D26]">{s.tel}</a>
              <a href={s.map} className="text-[#7B2D26] hover:underline">↗ Open in Google Maps</a>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Appointment form — full width band, formal */}
      <section className="bg-[#0E0E0E] text-[#F2EBDA]">
        <div className="max-w-[1100px] mx-auto px-8 lg:px-12 py-20 grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#A88B4F] mb-3">— Booking</p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-6">Reserve a private <span className="italic text-[#A88B4F]">viewing</span>.</h2>
            <p className="text-base text-[#F2EBDA]/55 leading-relaxed mb-8">Tell us which salon, when, and what you would like to see. We'll have the pieces on the desk when you arrive, and a coffee ready.</p>
            <div className="flex items-start gap-3 font-mono text-[11px] text-[#F2EBDA]/55">
              <Mail className="w-4 h-4 mt-0.5 text-[#A88B4F]" strokeWidth={1.2} />
              <div>
                <p className="text-[#A88B4F] tracking-[0.25em] uppercase mb-1">Or write directly</p>
                <p>concierge@vermillion.horology</p>
              </div>
            </div>
          </div>
          <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="watch-visit-name" placeholder="Your name" className="bg-transparent border-b border-[#F2EBDA]/30 py-3 text-base outline-none focus:border-[#A88B4F] placeholder:text-[#F2EBDA]/35" />
              <input type="email" placeholder="Email" className="bg-transparent border-b border-[#F2EBDA]/30 py-3 text-base outline-none focus:border-[#A88B4F] placeholder:text-[#F2EBDA]/35" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <select className="bg-transparent border-b border-[#F2EBDA]/30 py-3 text-base outline-none focus:border-[#A88B4F] text-[#F2EBDA]/85">
                <option className="bg-[#0E0E0E]">Salon — Geneva</option><option className="bg-[#0E0E0E]">Salon — London</option><option className="bg-[#0E0E0E]">Salon — Singapore</option>
              </select>
              <input type="date" className="bg-transparent border-b border-[#F2EBDA]/30 py-3 text-base outline-none focus:border-[#A88B4F] text-[#F2EBDA]/85" />
            </div>
            <input placeholder="Reference numbers of interest, if any" className="w-full bg-transparent border-b border-[#F2EBDA]/30 py-3 text-base outline-none focus:border-[#A88B4F] placeholder:text-[#F2EBDA]/35" />
            <textarea placeholder="Anything else?" rows="3" className="w-full bg-transparent border-b border-[#F2EBDA]/30 py-3 text-base outline-none focus:border-[#A88B4F] placeholder:text-[#F2EBDA]/35 resize-none" />
            <button type="button" data-testid="watch-visit-submit" className="bg-[#A88B4F] text-[#0E0E0E] px-8 py-4 font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-[#F2EBDA] transition-colors duration-500">Request appointment →</button>
          </form>
        </div>
      </section>
    </div>
  );
}
